<script lang="ts">
  import { fade, fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import NotifyMe from '$components/notify-me.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import Button from '$components/ui/button.svelte';
  import RadioButton from '$components/ui/radio-button.svelte';
  import { t } from '$lib/i18n';
  import { escapeKey } from '$lib/actions/escape-key';
  import { focusTrap } from '$lib/actions/focus-trap';
  import { clickOutside } from '$lib/actions/click-outside';
  import type {
    GetProductCardQuery,
    ProductCardFragment,
    QuickAddToCartFragment
  } from '$gql/storefront/operations';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import type { ProductAlertType } from '$components/pdp/alerts';
  import ShopifyPlans from '$components/pdp/variants/shopify-plans.svelte';

  export let product: ProductCardFragment | QuickAddToCartFragment;
  export let disabled: boolean;
  export let handleAddToCart: () => void;
  export let isToNotify: boolean;
  export let alert: ProductAlertType | undefined;
  export let selectedProductVariantStock: number;
  export let selectedProductVariant:
    | NonNullable<GetProductCardQuery['product']>['variants']['nodes'][number]
    | undefined
    | null;
  export let isLimitedEdition: boolean;
  export let selectedOptions: Record<string, string>;
  export let loading: boolean;
  export let showAdded: boolean;
  export let onClose: () => void;
  export let selectedPlanId: string | undefined;
  export let isPreSale: boolean | undefined = undefined;

  $: sellingPlans =
    product.sellingPlanGroups.edges.length > 0
      ? product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges.map((e) => e.node)
      : [];
</script>

<div
  class="absolute inset-0 z-20 rounded-sm flex flex-col p-0 overflow-hidden"
  use:escapeKey={{ id: 'add-to-card' + product.id, callback: onClose }}
  use:focusTrap
  role="dialog"
  aria-labelledby="variant-selection"
>
  <div
    transition:fly={{
      duration: $reducedMotion ? 0 : 300,
      y: '100%',
      opacity: 100,
      easing: cubicOut
    }}
    use:clickOutside={onClose}
    class="bg-highlight shadow mt-auto w-full rounded-sm overflow-y-auto"
  >
    <div class="p-2">
      <div class="flex justify-between items-center py-0.5">
        <p class="text-md" id="variant-selection">
          {#if isToNotify}
            {$t('pdp.out-of-stock.notify.item-out-of-stock')}
          {:else if product.options.length === 1}
            {$t('product-card.choose-variant-var', {
              variant: product.options[0].name.toLowerCase()
            })}
          {:else}
            {$t('product-card.choose-variant')}
          {/if}
        </p>
        <button
          type="button"
          class="flex outline-none border-none focus-visible:ring rounded-xs motion-safe:transition-shadow"
          on:click={onClose}
        >
          <Icon icon="cross" size="md" />
        </button>
      </div>
      {#if isToNotify && product?.variants?.nodes?.length == 1 && product.variants.nodes[0]}
        <NotifyMe
          variant={product.variants.nodes[0].id}
          productId={product.id}
          showWishlistButton={false}
        />
      {:else}
        <div class="mt-8 flex flex-col gap-4">
          {#each product.options as option, i}
            <div>
              {#if product.options.length > 1 || sellingPlans.length > 0}
                <p class="mb-2 text-sm text-primary">{option.name}</p>
              {/if}
              <form
                on:change={(e) => {
                  e.preventDefault();
                }}
                class="flex flex-wrap gap-2"
              >
                {#each option.values as value}
                  {@const currentVariant = product.variants.nodes.filter(
                    (v) => v.title === value
                  )[0]}
                  {@const qnt = currentVariant?.quantityAvailable}
                  <RadioButton
                    {value}
                    variant="ghost"
                    disabled={isLimitedEdition && qnt === 0}
                    label={value}
                    id={option.name + value + 'add-to-cart'}
                    bind:group={selectedOptions[option.name]}
                    class="variant-option"
                  />
                {/each}
              </form>

              {#if i === product.options.length - 1}
                <p class="mt-2 text-sm/none text-primary/80 h-5">
                  {#if alert}
                    <span
                      class="flex items-center gap-1"
                      transition:fade={{ duration: $reducedMotion ? 0 : 100 }}
                    >
                      <Icon size="md" icon="alert-hexagon-bold" />
                      {$t(alert, {
                        count: selectedProductVariantStock.toString()
                      })}
                    </span>
                  {/if}
                </p>
              {/if}
            </div>
          {/each}

          {#if sellingPlans.length > 0 && selectedPlanId}
            <ShopifyPlans {product} variant="ghost" bind:selectedPlanId />
          {/if}
        </div>
        {#if disabled && selectedProductVariant}
          <NotifyMe
            variant={selectedProductVariant.id}
            productId={product.id}
            showWishlistButton={false}
          />
        {:else}
          <Button
            type="button"
            class="w-full mt-2"
            size="sm"
            name="Add to Cart"
            on:click={handleAddToCart}
            {loading}
            {disabled}
          >
            {isPreSale
              ? $t('pdp.pre-sale')
              : showAdded
                ? $t('products.added')
                : $t('products.add-to-cart')}
          </Button>
        {/if}
      {/if}
    </div>
  </div>
</div>
